import {
	CenterTextPluginOptions,
	ExportReportInfo,
	ExportResult,
	ExportType,
	FileExtensionMap,
	FormatType,
	InstitutionReportEmailData,
	YearSelection,
} from "./report-types";
import { Chart, ChartType, Plugin, registerables } from "chart.js";
import i18n from "../../i18n";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import apiClient from "../../helpers/api-client";
import { validateEmail } from "../../helpers/email";
import { triggerFileDownload } from "../../helpers/download";
import type { ReportResult, ReportType } from "@verity/api-client";

declare module "chart.js" {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface PluginOptionsByType<TType extends ChartType> {
		centerText?: CenterTextPluginOptions;
	}
}

export function registerPlugin() {
	const centerTextPlugin: Plugin = {
		id: "centerText",
		afterDatasetsDraw(chart, _args, options) {
			const opts = options as unknown as CenterTextPluginOptions;
			if (opts.display) {
				const { width, height, ctx } = chart;
				const text = opts.text || "";
				const lines = text.split("\n");
				ctx.restore();

				const fontSize = 16;
				ctx.font = `normal ${fontSize}px 'Inter', sans-serif`;

				// First line (e.g., "Total")
				const textX = Math.round((width - ctx.measureText(lines[0]).width) / 2);
				const textY = opts.semiCircle ? height / 2 + 25 : height / 2 - 15;
				ctx.fillText(lines[0], textX, textY);

				// Second line (e.g., "100")
				if (lines[1]) {
					const textX2 = Math.round((width - ctx.measureText(lines[1]).width) / 2);
					ctx.fillText(lines[1], textX2, textY + (opts.smallText ? 18 : 25));
				}
				ctx.save();
			}
		},
	};
	Chart.register(...registerables);
	Chart.register(centerTextPlugin);
}

export function getExportModalDropDownLabel(value: YearSelection) {
	if (value === "current") {
		return i18n.t("instructor.components.reports.exportModal.currentDateRange");
	} else {
		return i18n.t("instructor.components.reports.exportModal.specificDateRange", { year: value });
	}
}

export function capitalizeFirstLetter(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getMonthYearRange = (startDate: Date, endDate: Date) => {
	const result: number[] = [];
	const currentDate = new Date(startDate);
	while (currentDate < endDate) {
		result.push(currentDate.getTime());
		currentDate.setMonth(currentDate.getMonth() + 1);
	}
	return result;
};

export function getFlagSeverity(flagSeverity: number): string {
	return flagSeverity <= 0
		? i18n.t("instructor.components.reports.noFlags")
		: flagSeverity <= 1
			? i18n.t("instructor.components.reports.minor")
			: flagSeverity <= 2
				? i18n.t("instructor.components.reports.major")
				: i18n.t("instructor.components.reports.severe");
}

export function getEmailError(email: string) {
	if (email === "") {
		return i18n.t("instructor.components.reports.exportModal.errors.emptyEmail");
	} else if (!validateEmail(email)) {
		return i18n.t("instructor.components.reports.exportModal.errors.invalidEmail");
	}
	return undefined; // No error
}

function formatMonthYear(date: Date): string {
	return date.toLocaleString("en-US", { month: "long", year: "numeric" });
}

// Only ever called for "institution" and "course" reports (convertToJSON skips this for "assessment"),
// and both want the same shape.
function getCumulativeJson(monthlyReport: ReportResult["monthlyReport"], startDate: Date) {
	if (!monthlyReport) return [];
	const months = monthlyReport.totalVeritySessions.length;
	const cumulativeEntries = [];

	for (let i = 0; i < months; i++) {
		const current = new Date(startDate);
		current.setMonth(startDate.getMonth() + i);

		const severe = monthlyReport.severeFlagCounts[i];
		const major = monthlyReport.majorFlagCounts[i];
		const minor = monthlyReport.minorFlagCounts[i];
		cumulativeEntries.push({
			Date: formatMonthYear(current),
			[i18n.t("instructor.components.reports.stats.cumulative.scoreAndFlags.avgIntegrityScore")]:
				monthlyReport.averageIntegrityScores[i] !== null ? Math.round(monthlyReport.averageIntegrityScores[i]!) : null,
			[i18n.t("instructor.components.reports.stats.cumulative.scoreAndFlags.totalFlaggedEvents")]:
				severe + major + minor,
			[i18n.t("instructor.components.reports.stats.cumulative.scoreAndFlags.severe")]: severe,
			[i18n.t("instructor.components.reports.stats.cumulative.scoreAndFlags.major")]: major,
			[i18n.t("instructor.components.reports.stats.cumulative.scoreAndFlags.minor")]: minor,
		});
	}
	return cumulativeEntries;
}

function getOverallTotals(reportResult: ReportResult, reportType: ReportType) {
	let json;

	if (reportType === "assessment") {
		json = [
			{
				[i18n.t("instructor.components.reports.stats.totals.assessment.avgIntegrityScore")]:
					(reportResult.averageIntegrityScore?.total && Math.round(reportResult.averageIntegrityScore.total)) ?? null,
				[i18n.t("instructor.components.reports.stats.totals.assessment.totalTestTakers")]:
					reportResult.students?.total ?? null,
				[i18n.t("instructor.components.reports.stats.totals.assessment.numAttempts")]:
					reportResult.veritySessions?.total ?? null,
				[i18n.t("instructor.components.reports.stats.totals.assessment.totalFlaggedEvents")]:
					reportResult.flaggedEvents?.total ?? null,
			},
		];
	} else if (reportType === "institution") {
		json = [
			{
				[i18n.t("instructor.components.reports.stats.totals.institution.avgIntegrityScore")]:
					(reportResult.averageIntegrityScore?.total && Math.round(reportResult.averageIntegrityScore.total)) ?? null,
				[i18n.t("instructor.components.reports.stats.totals.institution.totalTestTakers")]:
					reportResult.students?.total ?? null,
				[i18n.t("instructor.components.reports.stats.totals.institution.totalAssessments")]:
					reportResult.assessments?.total ?? null,
				[i18n.t("instructor.components.reports.stats.totals.institution.totalFlaggedEvents")]:
					reportResult.flaggedEvents?.total ?? null,
			},
		];
	} else {
		json = [
			{
				[i18n.t("instructor.components.reports.stats.totals.course.avgIntegrityScore")]:
					(reportResult.averageIntegrityScore?.total && Math.round(reportResult.averageIntegrityScore.total)) ?? null,
				[i18n.t("instructor.components.reports.stats.totals.course.numAttempts")]:
					reportResult.veritySessions?.total ?? null,
				[i18n.t("instructor.components.reports.stats.totals.course.avgAttempts")]:
					(reportResult.averageSessions?.total && Math.round(reportResult.averageSessions?.total)) ?? null,
				[i18n.t("instructor.components.reports.stats.totals.course.totalAssessments")]:
					reportResult.assessments?.total ?? null,
				[i18n.t("instructor.components.reports.stats.totals.course.totalTestTakers")]:
					reportResult.students?.total ?? null,
			},
		];
	}

	return json;
}

function getTableJson(reportType: ReportType, reportInfo: ReportResult) {
	const tableJson: Record<string, unknown>[] = [];

	const header =
		reportType === "course"
			? i18n.t("instructor.components.reports.stats.table.course.title")
			: reportType === "institution"
				? i18n.t("instructor.components.reports.stats.table.institution.title")
				: i18n.t("instructor.components.reports.stats.table.assessment.title");

	if (reportType === "course" && reportInfo.assessmentTableReport) {
		const table = reportInfo.assessmentTableReport;
		for (const tableRow of table) {
			tableJson.push({
				[i18n.t("instructor.components.reports.stats.table.course.assessmentName")]: tableRow.quizName,
				[i18n.t("instructor.components.reports.stats.table.course.testTaker")]: tableRow.studentCount,
				[i18n.t("instructor.components.reports.stats.table.course.flaggedEvents")]: tableRow.flaggedEvents,
				[i18n.t("instructor.components.reports.stats.table.course.avgIntegrityScore")]:
					tableRow.averageIntegrityScore !== null ? Math.round(tableRow.averageIntegrityScore) : null,
			});
		}
	} else if (reportType === "institution" && reportInfo.courseTableReport) {
		const table = reportInfo.courseTableReport;
		for (const tableRow of table) {
			tableJson.push({
				[i18n.t("instructor.components.reports.stats.table.institution.lmsCourseId")]: tableRow.lmsCourseId,
				[i18n.t("instructor.components.reports.stats.table.institution.courseName")]: tableRow.courseName,
				[i18n.t("instructor.components.reports.stats.table.institution.testTakers")]: tableRow.studentCount,
				[i18n.t("instructor.components.reports.stats.table.institution.assessmentsProctored")]:
					tableRow.assessmentCount,
				[i18n.t("instructor.components.reports.stats.table.institution.avgIntegrityScore")]:
					tableRow.averageIntegrityScore !== null ? Math.round(tableRow.averageIntegrityScore) : null,
			});
		}
	} else if (reportInfo.testTakerTableReport && reportType === "assessment") {
		const table = reportInfo.testTakerTableReport;
		for (const tableRow of table) {
			tableJson.push({
				[i18n.t("instructor.components.reports.stats.table.assessment.userID")]: tableRow.userId,
				[i18n.t("instructor.components.reports.stats.table.assessment.testTaker")]:
					tableRow.fullName ?? i18n.t("instructor.components.reports.stats.table.assessment.unknownName"),
				[i18n.t("instructor.components.reports.stats.table.assessment.attempts")]: tableRow.attempts,
				[i18n.t("instructor.components.reports.stats.table.assessment.status")]:
					tableRow.status ?? i18n.t("instructor.components.reports.stats.table.assessment.unknownStatus"),
				[i18n.t("instructor.components.reports.stats.table.assessment.startDate")]: tableRow.startDate,
				[i18n.t("instructor.components.reports.stats.table.assessment.avgIntegrityScore")]: Math.round(
					tableRow.averageIntegrityScore,
				),
			});
		}
	}
	return {
		[header]: tableJson,
	};
}

function convertToJSON({
	reportInfo,
	subAccountName,
	startDate,
	endDate,
	reportType,
}: {
	reportInfo: ReportResult;
	subAccountName?: string;
	startDate?: Date;
	endDate?: Date;
	reportType: ReportType;
}) {
	let filtersJson = null;

	// Assessment Report does not have start/end date
	if (startDate && endDate) {
		const filter = i18n.t("instructor.components.reports.stats.filters.filter");
		const value = i18n.t("instructor.components.reports.stats.filters.value");
		filtersJson = {
			Filters: [
				{
					[filter]: i18n.t("instructor.components.reports.stats.filters.startMonth"),
					[value]: startDate.getMonth() + 1,
				},
				{
					[filter]: i18n.t("instructor.components.reports.stats.filters.startYear"),
					[value]: startDate.getFullYear(),
				},
				{
					[filter]: i18n.t("instructor.components.reports.stats.filters.endMonth"),
					[value]: endDate.getMonth() + 1,
				},
				{
					[filter]: i18n.t("instructor.components.reports.stats.filters.endYear"),
					[value]: endDate.getFullYear(),
				},
				...(reportType === "institution"
					? [
							{
								[filter]: i18n.t("instructor.components.reports.stats.filters.subAccount"),
								[value]: subAccountName ?? i18n.t("instructor.components.reports.stats.filters.noSubAccount"),
							},
						]
					: []),
			],
		};
	}

	const overallTotals = {
		[i18n.t("instructor.components.reports.stats.totals.title")]: getOverallTotals(reportInfo, reportType),
	};

	let cumulativeScore = null;

	if (reportType !== "assessment" && startDate) {
		cumulativeScore = {
			[i18n.t("instructor.components.reports.stats.cumulative.title")]: getCumulativeJson(
				reportInfo.monthlyReport,
				startDate,
			),
		};
	}

	const tableJson = getTableJson(reportType, reportInfo);

	const fullReport = {
		...(filtersJson ?? {}),
		...overallTotals,
		...(cumulativeScore ?? {}),
		...tableJson,
	};

	return fullReport;
}

export async function exportReport({
	reportType,
	reportInfo,
	formatType,
	exportType,
	startDate,
	endDate,
	name,
	subAccountName,
	email,
}: {
	reportType: ReportType;
	reportInfo: ReportResult;
	formatType: FormatType;
	exportType: ExportType;
	startDate?: Date;
	endDate?: Date;
	name?: string;
	subAccountName?: string;
	email?: string;
}): Promise<ExportResult> {
	const safeName = (name ?? "").replace(/[<>:"/\\|?*]+/g, "_");
	const fileName = `${reportType}-report${safeName ? ` (${safeName})` : ""}.${FileExtensionMap[formatType]}`;

	const data = convertToJSON({
		reportInfo: reportInfo,
		startDate: startDate,
		endDate: endDate,
		reportType: reportType,
		subAccountName: subAccountName,
	});

	const blob = await getBlob(data, formatType);

	if (exportType === "download") {
		try {
			triggerFileDownload(blob, fileName);
			return { success: true };
		} catch (error) {
			console.error(error);
			return { success: false, error: i18n.t("instructor.components.reports.exportModal.errors.downloadFail") };
		}
	}

	if (exportType === "email") {
		if (!email) {
			return {
				success: false,
				error: i18n.t("instructor.components.reports.exportModal.errors.emailAddressRequired"),
			};
		}
		try {
			await sendReportEmail({
				blob: blob,
				startDate: startDate,
				endDate: endDate,
				reportType: reportType,
				fileName: fileName,
				email: email,
			});
			return { success: true };
		} catch (error) {
			return { success: false, error: i18n.t("instructor.components.reports.exportModal.errors.emailFail") };
		}
	}
	return { success: false, error: "Unknown export type." };
}

async function getBlob(data: Record<string, unknown>, formatType: FormatType) {
	switch (formatType) {
		case "json":
			return buildToJsonBlob(data);
		case "excel":
			return await buildExcelBlob(data);
		case "csv":
			return await buildCSVZipBlob(data);
		default:
			throw new Error(`Unsupported Format Type: ${formatType}`);
	}
}

function buildToJsonBlob(data: Record<string, unknown>) {
	const jsonStr = JSON.stringify(data, null, 2);
	return new Blob([jsonStr], { type: "application/json" });
}

async function buildExcelBlob(data: Record<string, unknown>) {
	const workbook = new ExcelJS.Workbook();

	for (const [sheetName, sheetData] of Object.entries(data)) {
		const worksheet = workbook.addWorksheet(sheetName);

		// Ensure sheetData is an array
		const rows = Array.isArray(sheetData) ? sheetData : [sheetData];

		// Structure is this -> first row = keys, 2nd row = values of the coresspondong keys.
		// Ex: OverallTotals
		if (rows.length === 1 && typeof rows[0] === "object" && !Array.isArray(rows[0])) {
			const obj = rows[0] as Record<string, unknown>;
			const keys = Object.keys(obj);
			const values = Object.values(obj);

			// Add keys row
			const headerRow = worksheet.addRow(keys);
			headerRow.font = { bold: true };

			// Add values row
			worksheet.addRow(values);
		} else {
			// Structure of json: Each object represent a row of data. In the object are key value pairs.
			// keys represent the header/first row.
			// Each key has a value -> that is the value of the column
			// Ex: Filters
			if (rows.length > 0 && typeof rows[0] === "object" && !Array.isArray(rows[0])) {
				// Add header row
				const keys = Object.keys(rows[0] as Record<string, unknown>);
				const headerRow = worksheet.addRow(keys);
				headerRow.font = { bold: true };

				// Add data rows
				(rows as Record<string, unknown>[]).forEach((row) => {
					worksheet.addRow(Object.values(row));
				});
			}
		}
		// Auto-size columns
		if (worksheet.columns) {
			worksheet.columns.forEach((col) => {
				if (!col) return; // For TS
				let maxLength = 0;
				col.eachCell?.({ includeEmpty: true }, (cell) => {
					const str = getCellStringValue(cell.value);
					maxLength = Math.max(maxLength, str.length);
				});
				col.width = Math.min(maxLength + 2, 50);
			});
		}
	}

	const excelBuffer = await workbook.xlsx.writeBuffer();
	return new Blob([excelBuffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
}

// Type script complaining that cell.value may be an object so this function returns a string.
function getCellStringValue(value: ExcelJS.CellValue): string {
	if (value == null) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number") return value.toString();
	if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
	if (value instanceof Date) return value.toISOString();

	if ("text" in (value as ExcelJS.CellHyperlinkValue)) {
		return (value as ExcelJS.CellHyperlinkValue).text ?? "";
	}
	if ("formula" in (value as ExcelJS.CellFormulaValue)) {
		return (value as ExcelJS.CellFormulaValue).formula ?? "";
	}
	if ("error" in (value as ExcelJS.CellErrorValue)) {
		return (value as ExcelJS.CellErrorValue).error ?? "";
	}
	if ("richText" in (value as ExcelJS.CellRichTextValue)) {
		return (value as ExcelJS.CellRichTextValue).richText.map((rt) => rt.text).join("");
	}

	return "";
}

async function buildCSVZipBlob(data: Record<string, unknown>) {
	const zip = new JSZip();

	for (const [sheetName, sheetData] of Object.entries(data)) {
		const rows = Array.isArray(sheetData) ? sheetData : [sheetData];
		let csvContent = "";

		// If it's a single object -> one header + one row
		if (rows.length === 1 && typeof rows[0] === "object" && !Array.isArray(rows[0])) {
			const obj = rows[0] as Record<string, unknown>;
			const keys = Object.keys(obj);
			const values = Object.values(obj);
			csvContent += keys.join(",") + "\n";
			csvContent += values.map(escapeCSV).join(",") + "\n";
		} else if (rows.length > 0 && typeof rows[0] === "object" && !Array.isArray(rows[0])) {
			// Multiple row objects with consistent keys
			const keys = Object.keys(rows[0] as Record<string, unknown>);
			csvContent += keys.join(",") + "\n";
			for (const row of rows as Record<string, unknown>[]) {
				csvContent += keys.map((k) => escapeCSV(row[k])).join(",") + "\n";
			}
		}

		// Add each sheet's CSV as its own file in the zip
		zip.file(`${sheetName}.csv`, csvContent);
	}

	const blob = await zip.generateAsync({ type: "blob" });
	return blob;
}

// Utility: escape CSV fields safely
function escapeCSV(value: unknown): string {
	if (value == null) return "";
	const str = String(value);
	if (str.includes(",") || str.includes('"') || str.includes("\n")) {
		return `"${str.replace(/"/g, '""')}"`; // escape quotes
	}
	return str;
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const result = reader.result as string;
			resolve(result.split(",")[1]); // remove "data:*/*;base64,"
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export async function sendReportEmail({
	blob,
	reportType,
	fileName,
	email,
	startDate,
	endDate,
}: {
	blob: Blob;
	reportType: ReportType;
	fileName: string;
	email: string;
	startDate?: Date;
	endDate?: Date;
}) {
	try {
		const base64Content = await blobToBase64(blob);
		let filterText: string | undefined;

		if (startDate && endDate) {
			filterText = i18n.t("instructor.components.reports.exportModal.dateRange", {
				startMonthYear: formatMonthYear(startDate),
				endMonthYear: formatMonthYear(endDate),
			});
		}
		await apiClient.report.sendReportEmail({
			toEmail: email,
			fileName: fileName,
			filters: filterText,
			contentType: blob.type,
			fileContent: base64Content,
			reportType: reportType,
		});
	} catch (error) {
		console.error(error);
	}
}

export async function getReportData({
	reportType,
	reportInfo,
	filterStartDate,
	filterEndDate,
}: {
	reportType: ReportType;
	reportInfo: ExportReportInfo;
	filterStartDate?: Date;
	filterEndDate?: Date;
}) {
	let data: ReportResult | undefined;
	let subFileName: string | undefined;

	// Preparing data for download/email
	if (reportInfo.institutionCourseReportInfo && filterStartDate && filterEndDate) {
		const { id, institutionInfo, courseInfo, verityFetch } = reportInfo.institutionCourseReportInfo;

		// Just cuz course id could be null
		if (!id) return null;

		// Get report data
		if (reportType === "course") {
			data = await fetchCourseReport({
				courseId: id,
				startDate: filterStartDate,
				endDate: filterEndDate,
				verityFetch: verityFetch,
			});
		} else if (reportType === "institution") {
			data = await fetchInstitutionReport({
				startDate: filterStartDate,
				endDate: filterEndDate,
				instituteId: id,
				subAccountId: institutionInfo?.subAccountId,
			});
		}

		subFileName = reportType === "institution" ? institutionInfo?.subAccountName : courseInfo?.courseName;
	}
	// Assessment Report
	else if (reportInfo.assessmentReportInfo) {
		data = reportInfo.assessmentReportInfo;
		subFileName = reportInfo.assessmentReportInfo.assessmentName;
	}

	return { data, subFileName };
}

export const fetchLastUpdatedDate = async (
	setLastUpdatedDate: (val: Date) => void,
	verityFetch: typeof fetch,
	instituteId: number | string,
	setShowRefresh: (val: boolean) => void,
) => {
	const lastUpdatedResponse = await verityFetch(`/api/verity/report/admin/lastUpdated/${instituteId}`);
	const lastUpdatedDate = new Date((await lastUpdatedResponse.json()) as string);
	setLastUpdatedDate(lastUpdatedDate);
	setShowRefresh(lastUpdatedDate && new Date().getTime() - lastUpdatedDate.getTime() > 3600000);
};

export async function fetchInstitutionReport({
	startDate,
	endDate,
	instituteId,
	subAccountId,
}: {
	startDate: Date;
	endDate: Date;
	instituteId: number;
	subAccountId?: number;
}) {
	try {
		const monthYearRange = getMonthYearRange(startDate, endDate);
		const data = await apiClient.report.getInstituteReport({
			instituteId,
			subAccountId,
			startDate: startDate,
			endDate: endDate,
			monthYearRange,
		});

		return data;
	} catch (error) {
		console.error(error);
	}
}

export async function fetchCourseReport({
	courseId,
	startDate,
	endDate,
	verityFetch,
}: {
	courseId: number;
	startDate: Date;
	endDate: Date;
	verityFetch: typeof fetch;
}) {
	const monthYearRange = getMonthYearRange(startDate, endDate);
	const params = new URLSearchParams({
		courseId: courseId.toString(),
		startDate: startDate.getTime().toString(),
		endDate: endDate.getTime().toString(),
		monthYearRange: JSON.stringify(monthYearRange),
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	});

	try {
		const report = await verityFetch(`/api/verity/report/admin/course-report?${params.toString()}`);
		return (await report.json()) as ReportResult;
	} catch (error) {
		console.error(error);
	}
}

export async function fetchAssessmentReport({
	assessmentId,
	verityFetch,
}: {
	assessmentId: number;
	verityFetch: typeof fetch;
}) {
	const params = new URLSearchParams({
		assessmentId: assessmentId.toString(),
	});

	try {
		const report = await verityFetch(`/api/verity/report/admin/assessment-report?${params.toString()}`);
		return (await report.json()) as ReportResult;
	} catch (error) {
		console.error(error);
	}
}

export type AiSummaryResponse = {
	summary: {
		title: string;
		description: string;
	};
	areaForImprovement: {
		itemType: "course" | "assessment" | "test-taker";
		items: {
			name: string;
			count: number;
			integrityScore: number | null;
		}[];
	};
	observedTrends: {
		title: string;
		description: string;
	};
	recommendedActions: {
		title: string;
		description: string;
	};
};

export async function fetchReportAiSummary(
	verityFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
	params: {
		instituteId?: number;
		subAccountId?: number;
		courseId?: number;
		assessmentId?: number;
		startDate: Date;
		endDate: Date;
		forceRegenerate?: boolean;
		type: ReportType;
	},
): Promise<AiSummaryResponse> {
	if (!params.assessmentId && !params.courseId && !params.subAccountId && !params.instituteId) {
		throw new Error(
			"fetchReportAiSummary: at least one of assessmentId, courseId, subAccountId, or instituteId is required",
		);
	}

	const body: Record<string, unknown> = {};
	if (params.instituteId) body.instituteId = params.instituteId;
	if (params.subAccountId) body.subAccountId = params.subAccountId;
	if (params.courseId) body.courseId = params.courseId;
	if (params.assessmentId) body.assessmentId = params.assessmentId;
	if (params.type) body.type = params.type;

	if (params.startDate) body.startDate = params.startDate.toISOString();
	if (params.endDate) body.endDate = params.endDate.toISOString();
	if (params.forceRegenerate) body.forceRegenerate = params.forceRegenerate;

	body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const response = await verityFetch(`/api/verity/report/admin/report-summary`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		throw new Error("Failed to fetch AI summary");
	}

	return (await response.json()) as AiSummaryResponse;
}

export async function fetchDistinctYearsForReport({
	id,
	verityFetch,
	reportType,
}: {
	id: string;
	verityFetch: typeof fetch;
	reportType: ReportType;
}) {
	try {
		if (reportType === "course") {
			const response = await verityFetch(`/api/verity/report/admin/course-report/available-years/${id}`);
			return (await response.json()) as number[];
		} else if (reportType === "institution") {
			const response = await verityFetch(`/api/verity/report/admin/institute-report/available-years/${id}`);
			return (await response.json()) as number[];
		} else {
			return [];
		}
	} catch (error) {
		console.error(error);
	}
}

export async function fetchUsageReport({
	instituteId,
	startDate,
	endDate,
	isAM,
}: {
	instituteId: number;
	startDate: { month: number; year: number };
	endDate: { month: number; year: number };
	isAM: boolean;
}) {
	try {
		const data = await apiClient.report.getUsageEmailsData({
			instituteId,
			startDate,
			endDate,
			isAM,
		});

		return data;
	} catch (error) {
		console.error(error);
	}
}

export async function generateHistoricEmailReport(email: InstitutionReportEmailData, canEditVeritySettings: boolean) {
	try {
		const emailHtml = await apiClient.report.generateHistoricEmailReport(email, canEditVeritySettings);

		return emailHtml;
	} catch (error) {
		console.error(error);
	}
}

export async function generateEmailPDF(html: string) {
	try {
		const emailPDF = await apiClient.report.generateEmailReportPDF(html);

		return emailPDF;
	} catch (error) {
		console.error(error);
	}
}
